import { usePublicFetch } from '@/composables/useApiFetch'
import type { Product, Category, FilterOptions, ProductSearchMeta, ProductSearchResponse } from '@/types'
import { logError } from '@/utils/errors'

interface BackendProduct {
  price_source?: string;
  id: number;
  sku: string;
  name: string;
  published: number | boolean;
  visible: number | boolean;
  short_description: string;
  description: string;
  featured: boolean;
  offer_start_date?: string;
  offer_end_date?: string;
  min_stock: number;
  stock: number;
  stock_available?: number | string;
  weight: string;
  length: string;
  width: string;
  height: string;
  offer_price?: string;
  price: string;
  category_id: number;
  brand_id: number;
  external_url: string;
  created_at: string;
  updated_at: string;
  // Currency fields
  currency_id?: number;
  currency_code?: string;
  currency_symbol?: string;
  currency_name?: string;
  original_price?: string | number;
  original_offer_price?: string | number | null;
  original_currency_id?: number;
  original_currency_code?: string;
  original_currency_symbol?: string;
  exchange_rate_buy?: string;
  exchange_rate_sell?: string;
  exchange_rate_date?: string;
  default_image?: string;
  category: {
    id: number;
    name: string;
    description?: string;
    path_image?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  brand: {
    id: number;
    name: string;
    created_at?: string;
    updated_at?: string;
  };
  attributes: Array<{
    id: number;
    name: string;
    pivot?: any;
  }>;
  // Support both field names from different endpoints
  especificaciones?: Array<{
    id: number;
    name: string;
    value: string;
  }>;
  specifications?: Array<{
    id: number;
    name: string;
    pivot?: {
      product_id: number;
      specification_id: number;
      value: string;
    };
  }>;
  especificaciones_text?: string;
  images: Array<{
    id: number;
    file_path: string;
    path_url?: string;
    is_main?: boolean;
    name?: string | null;
    imageable_id?: number;
    imageable_type?: string;
    created_at?: string;
    updated_at?: string;
  }>;
  stocks?: Array<{
    id: number;
    product_id: number;
    warehouse_id: number;
    warehouse_location_id?: number | null;
    batch_id?: number | null;
    quantity: string | number;
    created_at?: string;
    updated_at?: string;
  }>;
  product_prices?: Array<{
    id: number;
    product_id: number;
    price: string | number;
    price_type: string;
    is_offer_price: boolean;
    currency_id?: number | null;
    price_list_id?: number | null;
    customer_id?: number | null;
    warehouse_id?: number | null;
    exchange_rate_id?: number | null;
    margin_percent?: string | number | null;
    min_quantity?: number | null;
    max_quantity?: number | null;
    start_date?: string | null;
    end_date?: string | null;
    created_at?: string;
    updated_at?: string;
    price_list?: {
      id: number;
      name: string;
      code?: string;
      type?: 'percentage' | 'fixed' | string;
      adjustment_type?: 'discount' | 'increase' | string;
      adjustment_value?: string | number | null;
      valid_from?: string | null;
      valid_to?: string | null;
      is_default?: boolean;
      is_active?: boolean;
    } | null;
  }>;
  prices?: Array<{
    id: number;
    product_id: number;
    price: string | number;
    price_type: string;
    is_offer_price: boolean;
    currency_id?: number | null;
    price_list_id?: number | null;
    customer_id?: number | null;
    warehouse_id?: number | null;
    exchange_rate_id?: number | null;
    margin_percent?: string | number | null;
    min_quantity?: number | null;
    max_quantity?: number | null;
    start_date?: string | null;
    end_date?: string | null;
    created_at?: string;
    updated_at?: string;
    price_list?: {
      id: number;
      name: string;
      code?: string;
      type?: 'percentage' | 'fixed' | string;
      adjustment_type?: 'discount' | 'increase' | string;
      adjustment_value?: string | number | null;
      valid_from?: string | null;
      valid_to?: string | null;
      is_default?: boolean;
      is_active?: boolean;
    } | null;
  }>;
}

interface BackendCategory {
  id: number;
  name: string;
  description?: string;
  path_image?: string | null;
  path_image_url?: string | null;
  path_image_thumb_url?: string | null;
  created_at: string;
  updated_at: string;
}

interface BackendBrand {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface CategoriesResponse {
  categories: BackendCategory[];
  brands: BackendBrand[];
}
interface ProductsResponse {
  data: BackendProduct[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
    search_mode?: string;
    exact_match_found?: boolean;
  };
}

type BackendProductPrice = NonNullable<BackendProduct['product_prices']>[number]
type BackendPriceList = BackendProductPrice['price_list']

export class ProductsApiService {
  private $fetch = usePublicFetch()

  private hasIncompleteOfferPricing(backendProduct: BackendProduct): boolean {
    const listEntries = this.getBackendPriceEntries(backendProduct).filter(productPrice => productPrice.price_type === 'list')
    const hasListEntries = listEntries.length > 0
    const hasResolvedOriginalOfferPrice = Number(backendProduct.original_offer_price || 0) > 0
    const hasValidOfferPrice = Number(backendProduct.offer_price || 0) > 0 && Number(backendProduct.offer_price || 0) < Number(backendProduct.price || 0)
    const hasDetailedPriceListRules = listEntries.some(productPrice =>
      Boolean(productPrice.price_list?.adjustment_type && productPrice.price_list?.adjustment_value)
    )

    return hasListEntries && !hasResolvedOriginalOfferPrice && !hasValidOfferPrice && !hasDetailedPriceListRules
  }

  private mergeBackendProductPricing(primaryProduct: BackendProduct, fallbackProduct?: BackendProduct | null): BackendProduct {
    if (!fallbackProduct) {
      return primaryProduct
    }

    return {
      ...primaryProduct,
      offer_price: this.hasIncompleteOfferPricing(primaryProduct)
        ? (fallbackProduct.offer_price ?? primaryProduct.offer_price)
        : primaryProduct.offer_price,
      original_price: fallbackProduct.original_price ?? primaryProduct.original_price,
      original_offer_price: fallbackProduct.original_offer_price ?? primaryProduct.original_offer_price,
      original_currency_id: fallbackProduct.original_currency_id ?? primaryProduct.original_currency_id,
      original_currency_code: fallbackProduct.original_currency_code ?? primaryProduct.original_currency_code,
      original_currency_symbol: fallbackProduct.original_currency_symbol ?? primaryProduct.original_currency_symbol,
      product_prices: (fallbackProduct.product_prices && fallbackProduct.product_prices.length > 0)
        ? fallbackProduct.product_prices
        : (primaryProduct.product_prices ?? primaryProduct.prices),
      prices: primaryProduct.prices ?? fallbackProduct.prices,
    }
  }

  private async hydrateProductPricingFromCatalog(backendProduct: BackendProduct): Promise<BackendProduct> {
    if (!backendProduct.id || !this.hasIncompleteOfferPricing(backendProduct)) {
      return backendProduct
    }

    try {
      const response: ProductsResponse = await this.$fetch('products?per_page=1000')
      const catalogProduct = response.data.find(product =>
        product.id === backendProduct.id ||
        product.external_url === backendProduct.external_url
      )

      return this.mergeBackendProductPricing(backendProduct, catalogProduct)
    } catch (error) {
      logError('Error hydrating pricing for product', error, { productId: backendProduct.id })
      return backendProduct
    }
  }

  private toIntegerStock(value: unknown): number {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0
  }

  private resolveProductStock(backendProduct: BackendProduct): number {
    if (Array.isArray(backendProduct.stocks) && backendProduct.stocks.length > 0) {
      const totalStock = backendProduct.stocks.reduce((sum, stockItem) => {
        return sum + Number(stockItem.quantity || 0)
      }, 0)

      return this.toIntegerStock(totalStock)
    }

    if (backendProduct.stock_available !== undefined) {
      return this.toIntegerStock(backendProduct.stock_available)
    }

    return this.toIntegerStock(backendProduct.stock)
  }

  private isDateWithinRange(date: Date, startDate?: string | null, endDate?: string | null): boolean {
    const currentDate = new Date(date)
    currentDate.setHours(0, 0, 0, 0)

    const start = startDate ? new Date(`${startDate}T00:00:00`) : null
    const end = endDate ? new Date(`${endDate}T23:59:59`) : null

    if (start && Number.isNaN(start.getTime())) {
      return false
    }

    if (end && Number.isNaN(end.getTime())) {
      return false
    }

    if (start && currentDate < start) {
      return false
    }

    if (end && currentDate > end) {
      return false
    }

    return true
  }

  private resolveAdjustedPrice(
    priceValue: number,
    priceList?: BackendPriceList,
  ): number | undefined {
    if (!priceList?.adjustment_type || !priceList.adjustment_value) {
      return undefined
    }

    const adjustmentType = String(priceList.adjustment_type).toLowerCase()
    const adjustmentMode = String(priceList.type || '').toLowerCase()
    const adjustmentValue = Number(priceList.adjustment_value)

    if (!Number.isFinite(adjustmentValue) || adjustmentValue <= 0) {
      return undefined
    }

    if (adjustmentType === 'discount' || adjustmentType === 'descuento') {
      if (adjustmentMode === 'percentage' || adjustmentMode === 'porcentaje') {
        return priceValue * (1 - adjustmentValue / 100)
      }

      if (adjustmentMode === 'fixed' || adjustmentMode === 'fijo') {
        return priceValue - adjustmentValue
      }
    }

    return undefined
  }

  private getBackendPriceEntries(backendProduct: BackendProduct): BackendProductPrice[] {
    if (Array.isArray(backendProduct.product_prices) && backendProduct.product_prices.length > 0) {
      return backendProduct.product_prices
    }

    if (Array.isArray(backendProduct.prices) && backendProduct.prices.length > 0) {
      return backendProduct.prices as BackendProductPrice[]
    }

    return []
  }

  private normalizePriceAmount(
    backendProduct: BackendProduct,
    amount: number,
    entryCurrencyId?: number | null,
  ): number {
    if (!Number.isFinite(amount) || amount <= 0) {
      return 0
    }

    if (!entryCurrencyId || entryCurrencyId === backendProduct.currency_id) {
      return amount
    }

    const exchangeRate = Number(backendProduct.exchange_rate_sell || backendProduct.exchange_rate_buy || 0)

    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
      return amount
    }

    if (
      entryCurrencyId === backendProduct.original_currency_id &&
      backendProduct.original_currency_code === 'USD' &&
      backendProduct.currency_code === 'PYG'
    ) {
      return amount * exchangeRate
    }

    if (
      entryCurrencyId === backendProduct.original_currency_id &&
      backendProduct.original_currency_code === 'PYG' &&
      backendProduct.currency_code === 'USD'
    ) {
      return amount / exchangeRate
    }

    return amount
  }

  private resolveProductPricing(backendProduct: BackendProduct): { price: number; offerPrice?: number } {
    const basePrice = Number(backendProduct.price || 0)
    const fallbackPrice = Number.isFinite(basePrice) ? basePrice : 0
    // The API has already selected the active list and converted its stored price.
    // Applying the list percentage again would discount the same product twice.
    if (backendProduct.price_source) {
      const offer = Number(backendProduct.offer_price)
      return {
        price: fallbackPrice,
        offerPrice: Number.isFinite(offer) && offer > 0 && offer < fallbackPrice ? offer : undefined,
      }
    }
    const today = new Date()
    const validPrices = this.getBackendPriceEntries(backendProduct).filter(productPrice => {
      const isEntryValid = this.isDateWithinRange(today, productPrice.start_date, productPrice.end_date)
      const isListValid = productPrice.price_list
        ? productPrice.price_list.is_active !== false && this.isDateWithinRange(
            today,
            productPrice.price_list.valid_from,
            productPrice.price_list.valid_to,
          )
        : true

      return isEntryValid && isListValid
    })

    const explicitOfferPrice = backendProduct.offer_price ? Number(backendProduct.offer_price) : undefined
    const effectiveBasePrice = fallbackPrice

    const prioritizedPromotionalEntries = [...validPrices]
      .filter(productPrice =>
        productPrice.is_offer_price ||
        productPrice.price_type === 'offer' ||
        productPrice.price_type === 'list',
      )
      .sort((left, right) => {
        const getPriority = (productPrice: NonNullable<typeof validPrices[number]>) => {
          if (productPrice.is_offer_price) return 1
          if (productPrice.price_type === 'offer') return 2
          if (
            productPrice.price_type === 'list' &&
            ['discount', 'descuento'].includes(String(productPrice.price_list?.adjustment_type || '').toLowerCase())
          ) return 3
          if (productPrice.price_type === 'list') return 4
          return 99
        }

        const priorityDiff = getPriority(left) - getPriority(right)

        if (priorityDiff !== 0) {
          return priorityDiff
        }

        const leftEntryPrice = Number(left.price || 0)
        const rightEntryPrice = Number(right.price || 0)
        const leftPrice = this.normalizePriceAmount(
          backendProduct,
          this.resolveAdjustedPrice(leftEntryPrice, left.price_list) ?? leftEntryPrice,
          left.currency_id,
        )
        const rightPrice = this.normalizePriceAmount(
          backendProduct,
          this.resolveAdjustedPrice(rightEntryPrice, right.price_list) ?? rightEntryPrice,
          right.currency_id,
        )

        return leftPrice - rightPrice
      })
    const promotionalEntry = prioritizedPromotionalEntries[0]

    let resolvedOfferPrice: number | undefined

    if (promotionalEntry) {
      const entryPrice = Number(promotionalEntry.price || 0)
      const priceList = promotionalEntry.price_list
      const normalizedEntryPrice = this.normalizePriceAmount(
        backendProduct,
        entryPrice,
        promotionalEntry.currency_id,
      )
      resolvedOfferPrice = this.normalizePriceAmount(
        backendProduct,
        this.resolveAdjustedPrice(entryPrice, priceList) ?? entryPrice,
        promotionalEntry.currency_id,
      )

      if (
        (!resolvedOfferPrice || resolvedOfferPrice <= 0) &&
        Number.isFinite(normalizedEntryPrice) &&
        normalizedEntryPrice > 0
      ) {
        resolvedOfferPrice = normalizedEntryPrice
      }

      if ((!resolvedOfferPrice || resolvedOfferPrice <= 0) && Number.isFinite(entryPrice) && entryPrice > 0) {
        resolvedOfferPrice = this.normalizePriceAmount(
          backendProduct,
          entryPrice,
          promotionalEntry.currency_id,
        )
      }
    }

    if (
      (!resolvedOfferPrice || resolvedOfferPrice <= 0) &&
      explicitOfferPrice &&
      explicitOfferPrice > 0 &&
      explicitOfferPrice < effectiveBasePrice
    ) {
      resolvedOfferPrice = explicitOfferPrice
    }

    if (resolvedOfferPrice !== undefined) {
      resolvedOfferPrice = Number(resolvedOfferPrice.toFixed(4))
    }

    return {
      price: effectiveBasePrice,
      offerPrice: resolvedOfferPrice !== undefined && resolvedOfferPrice < effectiveBasePrice
        ? resolvedOfferPrice
        : undefined,
    }
  }

  private transformProduct(backendProduct: BackendProduct): Product {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const pricing = this.resolveProductPricing(backendProduct)

    const resolveImageUrl = (image: { file_path?: string; path_url?: string }): string | null => {
      const rawPath = image.path_url || image.file_path;
      if (!rawPath) return null;

      if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
        return rawPath;
      }

      if (rawPath.startsWith('/storage/')) {
        return `${baseUrl}${rawPath}`;
      }

      const cleanPath = rawPath.replace(/^\/+/, '');
      return `${baseUrl}/storage/${cleanPath}`;
    };
    
    // Build full image URLs
    let images: string[] = [];
    
    // If default_image is provided, use it as the first image
    if (backendProduct.default_image) {
      if (backendProduct.default_image.startsWith('http://') || backendProduct.default_image.startsWith('https://')) {
        images.push(backendProduct.default_image);
      } else {
        images.push(`${baseUrl}/storage/${backendProduct.default_image.replace(/^\/+/, '')}`);
      }
    }
    
    // Add other images from the images array
    if (backendProduct.images && backendProduct.images.length > 0) {
      const additionalImages = backendProduct.images
        .map(img => resolveImageUrl(img))
        .filter((url): url is string => Boolean(url))
        .filter(url => {
          // Skip if it's the same as default_image URL
          return !images.includes(url);
        });
      
      images = images.concat(additionalImages);
    }
    
    // Ensure unique values only
    images = Array.from(new Set(images));

    // Handle specifications - backend may send 'specifications' or 'especificaciones'
    let especificaciones: Array<{ id: number; name: string; value: string; }> | undefined;
    
    if (backendProduct.specifications) {
      // Transform specifications with pivot data to flat structure
      especificaciones = backendProduct.specifications.map(spec => ({
        id: spec.id,
        name: spec.name,
        value: spec.pivot?.value || ''
      }));
    } else if (backendProduct.especificaciones) {
      especificaciones = backendProduct.especificaciones;
    }

    return {
      id: backendProduct.id.toString(),
      sku: backendProduct.sku,
      name: backendProduct.name,
      description: backendProduct.description,
      short_description: backendProduct.short_description,
      price: pricing.price,
      price_source: backendProduct.price_source,
      offer_price: pricing.offerPrice,
      images: images,
      category_id: backendProduct.category_id,
      brand_id: backendProduct.brand_id,
      stock: this.resolveProductStock(backendProduct),
      published: typeof backendProduct.published === 'boolean' ? (backendProduct.published ? 1 : 0) : backendProduct.published,
      visible: typeof backendProduct.visible === 'boolean' ? (backendProduct.visible ? 1 : 0) : backendProduct.visible,
      featured: backendProduct.featured,
      weight: backendProduct.weight,
      length: backendProduct.length,
      width: backendProduct.width,
      height: backendProduct.height,
      min_stock: backendProduct.min_stock,
      external_url: backendProduct.external_url,
      offer_start_date: backendProduct.offer_start_date,
      offer_end_date: backendProduct.offer_end_date,
      // Currency fields
      currency_id: backendProduct.currency_id,
      currency_code: backendProduct.currency_code,
      currency_symbol: backendProduct.currency_symbol,
      currency_name: backendProduct.currency_name,
      original_price: backendProduct.original_price !== undefined ? Number(backendProduct.original_price) : undefined,
      original_offer_price: backendProduct.original_offer_price !== undefined && backendProduct.original_offer_price !== null
        ? Number(backendProduct.original_offer_price)
        : undefined,
      original_currency_id: backendProduct.original_currency_id,
      original_currency_code: backendProduct.original_currency_code,
      original_currency_symbol: backendProduct.original_currency_symbol,
      exchange_rate_buy: backendProduct.exchange_rate_buy ? parseFloat(backendProduct.exchange_rate_buy) : undefined,
      exchange_rate_sell: backendProduct.exchange_rate_sell ? parseFloat(backendProduct.exchange_rate_sell) : undefined,
      exchange_rate_date: backendProduct.exchange_rate_date,
      default_image: backendProduct.default_image
        ? (backendProduct.default_image.startsWith('http://') || backendProduct.default_image.startsWith('https://')
          ? backendProduct.default_image
          : `${baseUrl}/storage/${backendProduct.default_image.replace(/^\/+/, '')}`)
        : undefined,
      category: backendProduct.category,
      brand: backendProduct.brand,
      product_prices: this.getBackendPriceEntries(backendProduct),
      stocks: backendProduct.stocks,
      attributes: backendProduct.attributes,
      especificaciones: especificaciones,
      especificaciones_text: backendProduct.especificaciones_text,
      images_data: backendProduct.images,
      createdAt: new Date(backendProduct.created_at),
      updatedAt: new Date(backendProduct.updated_at),
    };
  }

  normalizeProduct(backendProduct: BackendProduct): Product {
    return this.transformProduct(backendProduct)
  }

  private async enrichProductsForOffers(products: BackendProduct[]): Promise<BackendProduct[]> {
    return await Promise.all(products.map(async (product) => {
      const hasOfferAttribute = product.attributes?.some(attribute =>
        String(attribute.name || '').toLowerCase().includes('oferta')
      )

      const hasPricingData = this.getBackendPriceEntries(product).length > 0
      const hasOfferPrice = Number(product.offer_price || 0) > 0

      if ((!hasOfferAttribute && !hasOfferPrice) || hasPricingData || !product.external_url) {
        return product
      }

      try {
        const detailedProduct: BackendProduct = await this.$fetch(`products/slug/${product.external_url}`)
        return detailedProduct?.id ? detailedProduct : product
      } catch (error) {
        logError('Error enriching product offer data', error, { slug: product.external_url })
        return product
      }
    }))
  }

  private transformCategory(backendCategory: BackendCategory): Category {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const categoryImage = backendCategory.path_image_url
      || (backendCategory.path_image ? `${baseUrl}/storage/${backendCategory.path_image}` : undefined);
    
    return {
      id: backendCategory.id,
      name: backendCategory.name,
      description: backendCategory.description,
      path_image: categoryImage,
      path_image_thumb: backendCategory.path_image_thumb_url || categoryImage,
      created_at: backendCategory.created_at,
      updated_at: backendCategory.updated_at,
    };
  }

  async getProducts(params: {
    page?: number
    limit?: number
  } & Partial<FilterOptions>): Promise<ProductSearchResponse> {
    const searchParams = new URLSearchParams()
    
    // Add pagination
    if (params.page) searchParams.append('page', params.page.toString())
    if (params.limit) searchParams.append('per_page', params.limit.toString())
    
    // Add filters
    if (params.categories?.length) {
      params.categories.forEach(cat => searchParams.append('category_id[]', cat))
    }
    if (params.brands?.length) {
      params.brands.forEach(brand => searchParams.append('brand_id[]', brand))
    }
    if (params.minPrice !== undefined) searchParams.append('min_price', params.minPrice.toString())
    if (params.maxPrice !== undefined) searchParams.append('max_price', params.maxPrice.toString())
    if (params.inStock) searchParams.append('in_stock', 'true')
    if (params.sale) {
      searchParams.append('on_sale', 'true')
      searchParams.append('has_active_offer', 'true')
    }
    if (params.featured) searchParams.append('featured', 'true')
    if (params.search) searchParams.append('search', params.search)
    if (params.sortBy) searchParams.append('sort_by', params.sortBy)
    if (params.sortOrder) searchParams.append('sort_order', params.sortOrder)

    const response: ProductsResponse = await this.$fetch(`products?${searchParams.toString()}`)
    
    const filteredProducts = await this.enrichProductsForOffers(response.data)

    return {
      data: filteredProducts.map(product => this.transformProduct(product)),
      links: response.links,
      meta: {
        total: response.meta.total,
        per_page: response.meta.per_page,
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
        search_mode: response.meta.search_mode,
        exact_match_found: response.meta.exact_match_found,
      } satisfies ProductSearchMeta,
    }
  }

  async getProduct(id: string): Promise<Product> {
    const response: BackendProduct = await this.$fetch(`products/${id}`)
    
    if (!response || !response.id) {
      throw new Error(`Product with id ${id} not found or invalid response structure`)
    }

    const hydratedProduct = await this.hydrateProductPricingFromCatalog(response)

    return this.transformProduct(hydratedProduct)
  }

  async getProductBySlug(slug: string): Promise<Product | null> {
    try {
      const response = await this.$fetch(`products/slug/${slug}`)
      
      // The backend returns the product directly for single product endpoints
      if (!response || !response.id) {
        return null
      }

      const hydratedProduct = await this.hydrateProductPricingFromCatalog(response)

      return this.transformProduct(hydratedProduct)
    } catch (error) {
      logError('Error fetching product by slug', error, { slug })
      return null
    }
  }

  async getCategories(): Promise<{
    categories: Category[];
    brands: BackendBrand[];
    meta?: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
    };
  }> {
    const response: CategoriesResponse = await this.$fetch('categories')
    
    return {
      categories: response.categories.map(category => this.transformCategory(category)),
      brands: response.brands
    }
  }

  async getFeaturedProducts(): Promise<{
    data: Product[];
    meta: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
    };
  }> {
    try {
      const response: ProductsResponse = await this.$fetch('products?featured=1&per_page=20')
      
      return {
        data: response.data.map(product => this.transformProduct(product)),
        meta: {
          total: response.meta.total,
          per_page: response.meta.per_page,
          current_page: response.meta.current_page,
          last_page: response.meta.last_page
        }
      }
    } catch (error) {
      console.warn('Featured products endpoint not available, using fallback')
      const response: ProductsResponse = await this.$fetch('products?per_page=20')
      
      return {
        data: response.data
          .filter((product: BackendProduct) => product.featured)
          .map((product: BackendProduct) => this.transformProduct(product))
          .slice(0, 8),
        meta: {
          total: response.data.filter((product: BackendProduct) => product.featured).length,
          per_page: 8,
          current_page: 1,
          last_page: 1
        }
      }
    }
  }

  async getRelatedProducts(productId: string, categoryId?: number): Promise<Product[]> {
    try {
      const response: ProductsResponse = await this.$fetch(`products/${productId}/related`)
      return response.data.map(product => this.transformProduct(product))
    } catch (error) {
      console.warn('Related products endpoint not available, using fallback')
      
      // If we have categoryId, use it directly for fallback
      if (categoryId) {
        try {
          const response: ProductsResponse = await this.$fetch(`products?category_id[]=${categoryId}&per_page=8`)
          return response.data
            .filter(p => p.id.toString() !== productId)
            .map(product => this.transformProduct(product))
            .slice(0, 4)
        } catch (fallbackError) {
          logError('Error getting related products by category', fallbackError, { productId, categoryId })
          return []
        }
      }
      
      // Last resort: try to get product details for category (original fallback)
      try {
        const product = await this.getProduct(productId)
        const response: ProductsResponse = await this.$fetch(`products?category_id[]=${product.category_id}&per_page=8`)
        return response.data
          .filter(p => p.id.toString() !== productId)
          .map(product => this.transformProduct(product))
          .slice(0, 4)
      } catch (lastResortError) {
        logError('Error getting related products (last resort)', lastResortError, { productId })
        return []
      }
    }
  }

  async searchProducts(query: string, limit: number = 10): Promise<Product[]> {
    const response: ProductsResponse = await this.$fetch(`products?search=${encodeURIComponent(query)}&per_page=${limit}`)
    return response.data.map(product => this.transformProduct(product))
  }

  async getBrandsByCategories(categoryIds: number[]): Promise<BackendBrand[]> {
    if (categoryIds.length === 0) {
      // Si no hay categorías seleccionadas, obtener todas las marcas
      try {
        const response: CategoriesResponse = await this.$fetch('brands')
        return response.brands || []
      } catch (error) {
        logError('Error fetching all brands', error)
        return []
      }
    }

    // Obtener marcas filtradas por categorías
    try {
      const categoryIdsString = categoryIds.join(',')
      const response: BackendBrand[] = await this.$fetch(`brands?category_ids=${categoryIdsString}`)
      return response || []
    } catch (error) {
      logError('Error fetching brands by categories', error, { categoryIds })
      return []
    }
  }
}

export const productsApiService = new ProductsApiService()
